import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../components/role-play/apiService';
import { localizeContentText, ContentTranslations } from '../utils/contentTranslations';
import { uiLocale } from '../i18n/language';

export interface MessageRating {
  id: string;
  created_at: string;
  message: string;
  comment: string;
  speaker: 'user' | 'bot';
  upvote: boolean;
  message_index: number;
}

export interface TranscriptEntry {
  text: string;
  speaker: 'user' | 'bot';
  feedback?: string;
  rating?: boolean;
  feedbackComment?: string;
  timestamp?: number;
  message_rating?: MessageRating;
  /** The line in the session's other languages ({language: text}), for display; `text` is what was said. */
  translations?: Record<string, string>;
}

/** A single "Need Help?" training-content item attached to a feedback step. */
export interface TrainingContentDisplayItem {
  kind: 'image' | 'gif' | 'video' | 'link';
  title: string;
  description?: string;
  /** Signed URL (uploads) or external URL (links); null if it couldn't be resolved. */
  url?: string | null;
}

export interface FeedbackStep {
  number: number;
  name: string;
  feedback: string;
  included: boolean;
  isOptional?: boolean;
  /** The step's authored description from the teaching method — what the step asks of the teacher. */
  description?: string;
  trainingContent?: TrainingContentDisplayItem[];
}

/**
 * One de-escalation tool the teacher either reached for or didn't.
 *
 * Distinct from a FeedbackStep, and deliberately so: the six Corrective Teaching steps
 * are a *sequence* the teacher is scored against (they drive stepsCompleted and the
 * session score), while the tools are an unordered kit they may draw from as often as
 * the student escalates. Nothing here feeds the score.
 */
export interface FeedbackTool {
  number: number;
  name: string;
  /** Whether the transcript shows the teacher using it. */
  included: boolean;
  /** The model's explanation — a quote where the tool was used, "You missed this step." where it wasn't. */
  feedback: string;
}

export interface SocialSkillFeedback {
  skill: string;
  feedback: string;
  rating: string;
}

export interface SessionRating {
  id: string;
  upvote: boolean;
  comment?: string;
}

export interface ConfigInfo {
  base_config: { id: string; name: string };
  student_profile: { id: string; name: string };
  scenarios: { id: string; name: string };
  corrective_teaching_step: { id: string; name: string };
}

export type SessionStatus = 'pending' | 'in_progress' | 'completed' | 'failed' | 'unknown';

export function isFeedbackProcessing(status: SessionStatus): boolean {
  return status === 'pending' || status === 'in_progress';
}

/**
 * True when the session ended before any transcript/audio was ever captured
 * (e.g. the user closed the tab or lost connection immediately). In that case
 * transcription/feedback are guaranteed to fail again on retry, since there's
 * no underlying data to process.
 *
 * The load-bearing signal here is `actualSessionDurationSeconds`: it's only
 * ever written by the /complete call (app/gemini_direct/router.py), the same
 * call that saves the transcript — so its absence means nothing reached the
 * backend at all. `transcriptionStatus === 'failed'` alone is NOT sufficient
 * on its own: the retry-exhaustion path (app/workers/tasks.py) sets it on any
 * exception after max retries, including for a fully-recorded session whose
 * transcription failed for an unrelated reason (e.g. a Gemini outage). If
 * `actual_session_duration_seconds` is ever backfilled from
 * `session_end_time - session_start_time` instead of only being set on
 * /complete, this check needs revisiting — it would start matching sessions
 * that could have been retried.
 */
export function hasNoRecordingData(session: Pick<SessionDetail, 'transcriptionStatus' | 'actualSessionDurationSeconds' | 'conversations'>): boolean {
  return session.transcriptionStatus === 'failed'
    && !session.actualSessionDurationSeconds
    && session.conversations.length === 0;
}

export interface SessionDetail {
  id: string;
  sessionStartTime: number;
  userName: string;
  userEmail: string;
  userId: string;
  /** Student system: the class the session was practised in; null for adult training. */
  classId: string | null;
  /** Which try at a class assignment this session was; null outside class assignments. */
  attemptNo: number | null;
  studentProfileName: string;
  teachingMethodName: string;
  teachingMethodId: string;
  feedbackSummary: string;
  feedbackSteps: FeedbackStep[];
  /** Empty for any prompt whose feedback block doesn't evaluate tools — the section hides itself. */
  tools: FeedbackTool[];
  socialSkills: SocialSkillFeedback[];
  conversations: TranscriptEntry[];
  score: number;
  stepsCompleted: number;
  stepsTotal: number;
  actualSessionDurationSeconds: number;
  /** How the session ended (see backend end_reasons.py). Undefined for sessions
   *  that finished before the reason was recorded. */
  endReason?: string;
  sessionRating?: SessionRating;
  transcriptionId?: string;
  notes?: string;
  preferredName?: string;
  configInfo?: ConfigInfo;
  feedbackStatus: SessionStatus;
  transcriptionStatus: SessionStatus;
  avatarType: string;
  avatarSignedUrl?: string;
  promptName?: string;
  /** The language the session was held in: the transcript's original language. */
  sessionLanguage?: string;
  socialSkillName?: string;
  /** Concrete mode the session was practiced in (easy/medium/hard). */
  resolvedDifficultyMode?: string;
  /** What the user picked (may be "random"). */
  selectedDifficultyMode?: string;
  difficultySelectedBy?: string;
  difficultyLocked?: boolean;
  /** The transcript or feedback is being translated into this reader's language right now. */
  translationPending?: boolean;
  /** Student system: the attempt's grade, when there is one to show this reader. */
  grade?: SessionGrade | null;
  /** The AI's feedback is held back from this student until their grade is released. */
  feedbackWithheld?: boolean;
}

/** Student system: the grade on a class-assignment attempt (run-data's `grade`). The
 *  student gets it once released; the class's staff get `attempt_id` to review it. */
export interface SessionGrade {
  max_score: number;
  released: boolean;
  final_score?: number | null;
  comment?: string | null;
  released_at?: string | null;
  attempt_id?: string;
  class_id?: string;
}

interface RawRunData {
  run_id: string;
  translation_pending?: boolean;
  grade?: SessionGrade | null;
  /** The school holds the AI's feedback back from the student until the grade is released. */
  feedback_withheld?: boolean;
  roleplay_session: {
    id: string;
    session_start_time: string;
    score: number;
    steps_completed: number;
    steps_total: number;
    actual_session_duration_seconds: number;
    end_reason?: string | null;
    student_profile_name: string;
    teaching_method_name: string;
    teaching_method_id: string;
    notes?: string;
    feedback_status: SessionStatus;
    transcription_status: SessionStatus;
    user_id: string;
    user_display_name: string;
    prompt_name?: string;
    /** The prompt's name and step text in each language; see utils/promptTranslations. */
    prompt_translations?: ContentTranslations;
    language?: string;
    social_skill_name?: string;
    user_email: string;
    resolved_difficulty_mode?: string | null;
    selected_difficulty_mode?: string | null;
    difficulty_selected_by?: string | null;
    difficulty_locked?: boolean | null;
    [key: string]: unknown;
  } | null;
  transcription: {
    id: string;
    conversations: TranscriptEntry[];
    [key: string]: unknown;
  } | null;
  feedback: {
    steps: Array<{
      number?: number;
      name?: string;
      feedback: string;
      included: boolean | string;
      description?: string;
      training_content?: TrainingContentDisplayItem[];
    }> | null;
    summary: string;
    social_skills?: SocialSkillFeedback[];
    tools?: Array<{
      number?: number;
      name?: string;
      included?: boolean | string;
      feedback?: string;
    }>;
    [key: string]: unknown;
  } | null;
  session_ratings?: {
    id: string;
    upvote: boolean;
    comment?: string;
  };
  preferred_name?: string;
  config_info?: ConfigInfo;
  [key: string]: unknown;
}

const AVATAR_MAP: Record<string, string> = {
  'Ethan': 'boy',
  'Maria': 'girl',
  'Liam': 'boy1',
  'Jack': 'boy2',
  'Emma': 'girl1',
  'Olivia': 'girl2',
};

const GLB_TYPES = ['boy', 'boy1', 'boy2', 'girl', 'girl1', 'girl2'];

function deriveAvatarType(profileName: string, rawAvatarType?: string): string {
  if (rawAvatarType && GLB_TYPES.includes(rawAvatarType)) return rawAvatarType;
  if (AVATAR_MAP[profileName]) return AVATAR_MAP[profileName];
  return 'boy';
}

/**
 * Some feedback prompts mark a step optional in its name ("Negative Consequence(Optional)").
 * The label is prompt bookkeeping, not something to show the teacher. Mirrors
 * `strip_optional_label` in `backend/app/roleplay_feedback/feedback.py`, which strips it on
 * save; this covers sessions saved before that. Spanish too ("(Opcional)"), as a translated
 * step name carries it in the reader's language (the backend's OPTIONAL_WORDS).
 */
const OPTIONAL_SUFFIX = /\s*\(\s*(optional|opcional)\s*\)\s*$/i;

function stripOptionalLabel(name: string): string {
  return name.replace(OPTIONAL_SUFFIX, '');
}

/**
 * A feedback step's name in the UI language, without its "(Optional)" label. The prompt's
 * translations are keyed by its step titles, label included, while the feedback stores the
 * name with the label stripped, so a name with no exact entry is matched label-blind.
 */
export function localizeStepName(translations: ContentTranslations | undefined, name: string): string {
  const exact = localizeContentText(translations, name);
  if (exact !== name) return stripOptionalLabel(exact);
  const byText = translations?.[uiLocale()] ?? {};
  const bare = stripOptionalLabel(name);
  const key = Object.keys(byText).find(title => stripOptionalLabel(title) === bare);
  return stripOptionalLabel(key ? byText[key] : name);
}

/**
 * A step's description in the UI language. The prompt's translations are keyed by the
 * description as authored (often with a trailing newline), while the feedback carries it
 * trimmed, so a description with no exact entry is matched whitespace-blind.
 */
export function localizeStepDescription(translations: ContentTranslations | undefined, description: string): string {
  const text = description.trim();
  const exact = localizeContentText(translations, text);
  if (exact !== text) return exact;
  const byText = translations?.[uiLocale()] ?? {};
  const key = Object.keys(byText).find(original => original.trim() === text);
  return key ? byText[key].trim() : text;
}

/**
 * `translations` shows each step's name and description in the UI language: a step the
 * feedback matched to the prompt is stored under the prompt step's own title, which is
 * what the prompt's translations are keyed by.
 */
function parseSteps(rawSteps: unknown, translations?: ContentTranslations): FeedbackStep[] {
  if (Array.isArray(rawSteps)) {
    return rawSteps.map((step, index) => ({
      number: step.number ?? index + 1,
      name: step.name ? localizeStepName(translations, step.name) : `Step ${index + 1}`,
      feedback: step.feedback ?? '',
      included: step.included === true || step.included === 'true' || step.included === 'Yes',
      isOptional: step.is_optional === true || step.is_optional === 'true' || OPTIONAL_SUFFIX.test(step.name ?? ''),
      description: typeof step.description === 'string' && step.description.trim()
        ? localizeStepDescription(translations, step.description)
        : undefined,
      trainingContent: Array.isArray(step.training_content) ? step.training_content : undefined,
    })).sort((a, b) => a.number - b.number);
  }

  if (rawSteps && typeof rawSteps === 'object') {
    return Object.entries(rawSteps as Record<string, unknown>).map(([key, value], index) => {
      const v = value as { feedback?: string; included?: boolean | string; is_optional?: boolean | string } | null;
      return {
        number: index + 1,
        name: stripOptionalLabel(key),
        feedback: v?.feedback ?? '',
        included: v?.included === true || v?.included === 'true' || v?.included === 'Yes',
        isOptional: v?.is_optional === true || v?.is_optional === 'true' || OPTIONAL_SUFFIX.test(key),
      };
    });
  }

  if (rawSteps != null) {
    console.error('parseSteps: unexpected steps type', typeof rawSteps, rawSteps);
  }

  return [];
}

/**
 * `included` spellings that count as "the teacher used this tool".
 *
 * Kept identical to `_TRUTHY` in `backend/app/roleplay_feedback/tools.py`, and compared
 * case-folded the same way. Two lists that disagree are worse than one: a value the
 * server calls used ("partial", "mostly") rendering here as **Not Used** is a wrong
 * verdict shown to a teacher, not a missing one.
 */
const TRUTHY_INCLUDED = new Set([
  'true', 'yes', 'y', '1', 'fully', 'mostly', 'partial', 'partially', 'good',
]);

/**
 * Read the tool evaluation off a run payload.
 *
 * `included` is normalized on the server, but the same string spellings the raw Gemini
 * response uses are accepted here too — a session read straight out of raw_feedback by an
 * older backend would otherwise show every tool as used, since any non-empty string
 * (`"false"` included) is truthy.
 */
function parseTools(rawTools: unknown): FeedbackTool[] {
  if (!Array.isArray(rawTools)) return [];
  return rawTools
    .filter((tool) => tool && typeof tool === 'object' && typeof tool.name === 'string' && tool.name.trim())
    .map((tool, index) => ({
      number: typeof tool.number === 'number' ? tool.number : index + 1,
      name: tool.name.trim(),
      included: tool.included === true
        || (typeof tool.included === 'string' && TRUTHY_INCLUDED.has(tool.included.trim().toLowerCase())),
      feedback: typeof tool.feedback === 'string' ? tool.feedback : '',
    }))
    .sort((a, b) => a.number - b.number);
}

function mapRunDataToSessionDetail(data: RawRunData): SessionDetail {
  const session = data.roleplay_session;
  if (!session) {
    console.error('mapRunDataToSessionDetail: roleplay_session is missing in run data', data.run_id);
  }

  const feedback = data.feedback;
  const transcription = data.transcription;

  const promptTranslations = session?.prompt_translations;
  // Feedback is written in the session's language; a reader of another one sees each comment
  // translated once the background translation has run, and the original until then.
  const feedbackTranslations = feedback?.translations as ContentTranslations | undefined;
  const inReaderLanguage = (text: string) => localizeContentText(feedbackTranslations, text);
  const feedbackSteps = parseSteps(feedback?.steps ?? null, promptTranslations).map(step => ({
    ...step,
    feedback: inReaderLanguage(step.feedback),
  }));
  const tools = parseTools(
    feedback?.tools ?? (feedback?.metadata as { tools?: unknown } | undefined)?.tools ?? null,
  ).map(tool => ({ ...tool, feedback: inReaderLanguage(tool.feedback) }));
  const stepsTotal = session?.steps_total ?? 0;
  const stepsCompleted = session?.steps_completed ?? 0;

  const startTimeRaw = session?.session_start_time ?? (session as { created_at?: string } | null)?.created_at;
  const sessionStartTime = startTimeRaw ? new Date(startTimeRaw).getTime() : 0;

  return {
    id: session?.id ?? data.run_id,
    sessionStartTime,
    userName: session?.user_display_name ?? '',
    userEmail: session?.user_email ?? '',
    userId: session?.user_id ?? '',
    classId: (session?.class_id as string | null | undefined) ?? null,
    attemptNo: (session?.attempt_no as number | null | undefined) ?? null,
    studentProfileName: session?.student_profile_name ?? '',
    teachingMethodName: session?.teaching_method_name ?? '',
    teachingMethodId: session?.teaching_method_id ?? '',
    feedbackSummary: inReaderLanguage((feedback?.summary as string) ?? ''),
    feedbackSteps,
    tools,
    socialSkills: ((feedback?.social_skills as SocialSkillFeedback[]) ?? []).map(skill => ({
      ...skill,
      feedback: inReaderLanguage(skill.feedback),
    })),
    conversations: (transcription?.conversations as TranscriptEntry[]) ?? [],
    score: session?.score ?? 0,
    stepsCompleted,
    stepsTotal,
    actualSessionDurationSeconds: session?.actual_session_duration_seconds ?? 0,
    endReason: session?.end_reason ?? undefined,
    sessionRating: data.session_ratings ? {
      id: data.session_ratings.id,
      upvote: data.session_ratings.upvote,
      comment: data.session_ratings.comment,
    } : undefined,
    transcriptionId: transcription?.id,
    notes: session?.notes,
    preferredName: data.preferred_name,
    configInfo: data.config_info,
    feedbackStatus: session?.feedback_status ?? 'unknown',
    transcriptionStatus: session?.transcription_status ?? 'unknown',
    avatarType: deriveAvatarType(session?.student_profile_name ?? '', (session as { avatar_type?: string } | null)?.avatar_type),
    avatarSignedUrl: session?.avatar_signed_url as string | undefined,
    promptName: session?.prompt_name ? localizeContentText(promptTranslations, session.prompt_name) : undefined,
    sessionLanguage: session?.language ?? undefined,
    socialSkillName: session?.social_skill_name ?? undefined,
    resolvedDifficultyMode: session?.resolved_difficulty_mode ?? undefined,
    selectedDifficultyMode: session?.selected_difficulty_mode ?? undefined,
    difficultySelectedBy: session?.difficulty_selected_by ?? undefined,
    difficultyLocked: session?.difficulty_locked ?? undefined,
    translationPending: data.translation_pending === true,
    grade: data.grade ?? null,
    feedbackWithheld: data.feedback_withheld === true,
  };
}

const POLL_INTERVAL_MS = 3000;
// 30 * 3s = 90s. Feedback generation typically takes 15-30s (see
// FeedbackProcessingState's subtitle), so this still gives a healthy buffer
// before surfacing "taking longer than expected" — down from 180s, which left
// a trainer staring at a spinner for 3 minutes before getting any signal.
const MAX_POLL_RETRIES = 30;
// Halfway to MAX_POLL_RETRIES (45s): an early, non-terminal heads-up. The page
// keeps polling normally after this fires — it's just a toast telling the
// trainer generation is running a bit slow, distinct from pollingTimedOut's
// full "taking longer than expected" panel at 90s.
const SLOW_WARNING_AT_RETRY = Math.floor(MAX_POLL_RETRIES / 2);
// A session is translated the first time a reader of another language opens it. The page keeps
// loading and re-fetches until the backend says the worker is done: it then shows the
// translation, or the original if the translation failed. The backend stops calling a
// translation pending after 60s on its own (a worker that never reports); this cap only stops
// the page waiting if the backend cannot be reached.
const TRANSLATION_POLL_MS = 1000;
const MAX_TRANSLATION_POLLS = 60;

export function useSessionDetail(sessionId: string | undefined) {
  const [session, setSession] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  // Bumped (never reset to a "seen" value) each time the 45s mark is hit for a
  // *new* polling cycle, so a component can watch it with a plain useEffect
  // and fire a toast without needing its own "have I already shown this"
  // bookkeeping — see SessionDetailPage's effect on this value.
  const [slowWarningTick, setSlowWarningTick] = useState(0);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const translationPollsRef = useRef(0);
  const [awaitingTranslation, setAwaitingTranslation] = useState(false);

  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      setError('No session ID provided');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.get<RawRunData>(`/live-roleplay/run-data/${sessionId}`);
      setSession(mapRunDataToSessionDetail(data));
      setAwaitingTranslation(data.translation_pending === true);
      // Reset timeout flag on a manual fetch so the user can see fresh state
      setPollingTimedOut(false);
    } catch (err) {
      console.error('useSessionDetail: failed to fetch session', sessionId, err);
      const message = err instanceof Error ? err.message : 'Failed to load session';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const silentRefetch = useCallback(async () => {
    if (!sessionId) return;
    try {
      const data = await api.get<RawRunData>(`/live-roleplay/run-data/${sessionId}`);
      setSession(mapRunDataToSessionDetail(data));
    } catch {
      // Silent — don't disrupt UI during poll
    }
  }, [sessionId]);

  const retryFeedback = useCallback(async () => {
    if (!sessionId) return;
    try {
      await api.post(`/live-roleplay/session/${sessionId}/retry-feedback`, {});
      // Reset timeout flag and re-fetch so polling restarts on the fresh pending status
      setPollingTimedOut(false);
      await fetchSession();
    } catch (err) {
      console.error('useSessionDetail: failed to retry feedback', sessionId, err);
      // A 400 here can mean the backend just self-healed a stale status (the
      // session's feedback had actually already completed via the watchdog
      // or an earlier retry) rather than a real client error — it commits
      // the corrected state before returning that 400. Refetch regardless of
      // outcome so the page reflects it instead of looking like the Retry
      // button silently did nothing.
      await fetchSession();
    }
  }, [sessionId, fetchSession]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Notify the rest of the app (e.g. the navbar's streak/badge display) the moment
  // feedback finishes processing — that's also when the gamification pipeline
  // (streak/XP/badges) runs, so this is a much better trigger than a fixed poll.
  const wasProcessingRef = useRef(false);
  useEffect(() => {
    if (!session) return;
    const processing = isFeedbackProcessing(session.feedbackStatus);
    if (wasProcessingRef.current && !processing && session.feedbackStatus === 'completed') {
      window.dispatchEvent(new CustomEvent('gamification:refresh'));
      // process_analytics_task (streak/XP/badges) is enqueued right after feedback
      // completes and typically finishes a few seconds later — fire once more so we
      // don't miss badges awarded in that short window.
      setTimeout(() => window.dispatchEvent(new CustomEvent('gamification:refresh')), 4000);
    }
    wasProcessingRef.current = processing;
  }, [session]);

  // Polling: auto-refetch while feedback is processing.
  // Derive a stable boolean so the effect only fires on transitions.
  const shouldPoll = !!session && isFeedbackProcessing(session.feedbackStatus) && !pollingTimedOut && !hasNoRecordingData(session);

  useEffect(() => {
    if (!shouldPoll) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
      pollCountRef.current = 0;
      return;
    }

    // Starting a new polling cycle — reset timeout flag for this run
    setIsPolling(true);
    setPollingTimedOut(false);
    pollCountRef.current = 0;

    pollIntervalRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      if (pollCountRef.current === SLOW_WARNING_AT_RETRY) {
        setSlowWarningTick((tick) => tick + 1);
      }
      if (pollCountRef.current >= MAX_POLL_RETRIES) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsPolling(false);
        // Do one final fetch before declaring timeout so we don't miss a just-completed status
        await silentRefetch();
        setPollingTimedOut(true);
        return;
      }
      silentRefetch();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [shouldPoll, silentRefetch]);

  // Polling: re-fetch while this reader's translation is being made. Each re-fetch sets a new
  // session, which schedules the next, so requests never overlap. The feedback poll above
  // re-fetches too, so this waits while that one runs.
  useEffect(() => {
    if (!session?.translationPending) {
      translationPollsRef.current = 0;
      return;
    }
    if (translationPollsRef.current >= MAX_TRANSLATION_POLLS) {
      setAwaitingTranslation(false);
      return;
    }
    if (shouldPoll) return;
    const timer = setTimeout(() => {
      translationPollsRef.current += 1;
      silentRefetch();
    }, TRANSLATION_POLL_MS);
    return () => clearTimeout(timer);
  }, [session, shouldPoll, silentRefetch]);

  // Keep loading until the translation is done: landed, or failed and shown in the original.
  useEffect(() => {
    if (awaitingTranslation && !session?.translationPending) setAwaitingTranslation(false);
  }, [awaitingTranslation, session]);

  const updateSessionRating = (rating: SessionRating | undefined) => {
    if (session) {
      setSession({ ...session, sessionRating: rating });
    }
  };

  const updateConversation = (index: number, update: Partial<TranscriptEntry>) => {
    if (!session) return;
    const updated = [...session.conversations];
    updated[index] = { ...updated[index], ...update };
    setSession({ ...session, conversations: updated });
  };

  return {
    session,
    loading: loading || awaitingTranslation,
    error,
    isPolling,
    pollingTimedOut,
    slowWarningTick,
    refetch: fetchSession,
    retryFeedback,
    updateSessionRating,
    updateConversation,
  };
}
